import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  onSnapshot,
  orderBy,
  query,
  setDoc,
  updateDoc,
} from "firebase/firestore";
import {
  deleteObject,
  getDownloadURL,
  ref,
  uploadBytes,
} from "firebase/storage";
import { db, storage } from "../firebase/config";

const OFFICE_COLLECTION = "officeHolders";

export const subscribeToOfficeHolders = (onChange) => {
  if (!db) {
    onChange([]);
    return () => {};
  }
  const officeQuery = query(
    collection(db, OFFICE_COLLECTION),
    orderBy("order", "asc"),
  );
  return onSnapshot(officeQuery, (snapshot) => {
    const members = snapshot.docs.map((item) => ({
      id: item.id,
      ...item.data(),
    }));
    onChange(members);
  });
};

const uploadPhoto = async (file, holderId) => {
  if (!storage) {
    throw new Error("Firebase storage is not configured.");
  }
  const safeName = file.name
    ? file.name.replace(/[^a-zA-Z0-9_.-]/g, "_")
    : "photo";
  const storagePath = holderId
    ? `officeHolders/${holderId}/${Date.now()}_${safeName}`
    : `officeHolders/${Date.now()}_${safeName}`;
  const fileRef = ref(storage, storagePath);
  const uploadResult = await uploadBytes(fileRef, file);
  const downloadUrl = await getDownloadURL(uploadResult.ref);
  return { downloadUrl, storagePath };
};

const deletePhotoIfExists = async (storagePath) => {
  if (!storagePath || !storage) {
    return;
  }
  try {
    await deleteObject(ref(storage, storagePath));
  } catch (error) {
    if (process.env.NODE_ENV !== "production") {
      // eslint-disable-next-line no-console
      console.warn("Failed to delete office holder image:", error);
    }
  }
};

export const createOfficeHolder = async (payload) => {
  if (!db) {
    throw new Error("Firebase database is not configured.");
  }

  const { imageFile, order, ...rest } = payload;
  const collectionRef = collection(db, OFFICE_COLLECTION);
  const newDocRef = doc(collectionRef);

  let photoData = {};
  if (imageFile) {
    const { downloadUrl, storagePath } = await uploadPhoto(
      imageFile,
      newDocRef.id,
    );
    photoData = { photoUrl: downloadUrl, photoStoragePath: storagePath };
  }

  const computedOrder =
    typeof order === "number" ? order : order ? Number(order) : Date.now();

  await setDoc(newDocRef, {
    ...rest,
    ...photoData,
    order: Number.isFinite(computedOrder) ? computedOrder : Date.now(),
  });

  return newDocRef.id;
};

export const updateOfficeHolder = async (id, updates) => {
  if (!db) {
    throw new Error("Firebase database is not configured.");
  }

  const memberRef = doc(db, OFFICE_COLLECTION, id);
  const snapshot = await getDoc(memberRef);
  if (!snapshot.exists()) {
    throw new Error("Office bearer not found.");
  }

  const existing = snapshot.data();
  const { imageFile, removeImage, order, ...rest } = updates;
  const payload = { ...rest };

  if (order !== undefined) {
    const numericOrder = typeof order === "number" ? order : Number(order);
    if (Number.isFinite(numericOrder)) {
      payload.order = numericOrder;
    }
  }

  if (imageFile) {
    const { downloadUrl, storagePath } = await uploadPhoto(imageFile, id);
    payload.photoUrl = downloadUrl;
    payload.photoStoragePath = storagePath;
    if (
      existing.photoStoragePath &&
      existing.photoStoragePath !== storagePath
    ) {
      await deletePhotoIfExists(existing.photoStoragePath);
    }
  } else if (removeImage) {
    payload.photoUrl = null;
    payload.photoStoragePath = null;
    if (existing.photoStoragePath) {
      await deletePhotoIfExists(existing.photoStoragePath);
    }
  }

  await updateDoc(memberRef, payload);
};

export const deleteOfficeHolder = async (id) => {
  if (!db) {
    throw new Error("Firebase database is not configured.");
  }
  const memberRef = doc(db, OFFICE_COLLECTION, id);
  const snapshot = await getDoc(memberRef);
  if (snapshot.exists()) {
    const data = snapshot.data();
    if (data.photoStoragePath) {
      await deletePhotoIfExists(data.photoStoragePath);
    }
  }
  await deleteDoc(memberRef);
};
